import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Loader, MessageCircle } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { feedbackService, type QuickFeedbackRequest } from '../../services/feedbackService';

interface EmojiRatingProps {
  label: string;
  onRating?: (rating: number, comment?: string, context?: EmojiRatingContext) => void;
  className?: string;
  context?: EmojiRatingContext;
}

interface EmojiRatingContext {
  component: string;
  feature: string;
  assessmentId?: string;
  assessmentType?: string;
}

const EMOJI_RATINGS = [
  { rating: 5, emoji: '😍', label: 'Excelente', color: 'text-green-600' },
  { rating: 4, emoji: '😊', label: 'Muito bom', color: 'text-green-400' },
  { rating: 3, emoji: '😐', label: 'Ok', color: 'text-yellow-500' },
  { rating: 2, emoji: '😕', label: 'Ruim', color: 'text-orange-500' },
  { rating: 1, emoji: '😞', label: 'Muito ruim', color: 'text-red-500' },
];

export const EmojiRating: React.FC<EmojiRatingProps> = ({ 
  label, 
  onRating,
  className = "",
  context
}) => {
  const { user } = useAuthStore();
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExpanded && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [isExpanded]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (!isSubmitted && !isSubmitting) {
          setIsExpanded(false);
          setSelectedRating(null);
          setComment('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSubmitted, isSubmitting]);

  const handleEmojiClick = (rating: number) => {
    if (selectedRating === rating) {
      setSelectedRating(null);
      setIsExpanded(false);
    } else {
      setSelectedRating(rating);
      setIsExpanded(true);
    }
  };

  const handleSubmit = async () => {
    if (selectedRating === null) return;

    setIsSubmitting(true);
    try {
      // Send feedback to backend if context is provided
      if (context) {
        const quickFeedbackData: QuickFeedbackRequest = {
          rating: selectedRating,
          comment: comment || undefined,
          context,
          userContext: user ? {
            id: user.id || '',
            name: user.name,
            email: user.email,
            profileImage: user.profile_image,
            subscriptionTier: user.subscription_tier,
          } : undefined,
          submittedAt: new Date().toISOString(),
          userAgent: navigator.userAgent,
        };

        await feedbackService.createQuickFeedback(quickFeedbackData);
      }
      
      // Call parent callback
      onRating?.(selectedRating, comment, context);
      setIsSubmitted(true);

      // Reset after showing success
      setTimeout(() => {
        setIsExpanded(false);
        setIsSubmitted(false);
        setSelectedRating(null);
        setComment('');
      }, 2000);
    } catch (error) {
      console.error('Error submitting rating:', error);
      // Still call parent callback even if backend fails
      onRating?.(selectedRating, comment, context);
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedEmoji = EMOJI_RATINGS.find(e => e.rating === selectedRating);

  return (
    <div 
      ref={containerRef}
      className={`
        bg-white border border-gray-200 shadow-sm rounded-xl w-full
        transition-all duration-300 ease-in-out overflow-hidden
        ${isExpanded ? 'max-h-[340px]' : 'max-h-20 sm:max-h-14'}
        ${className}
      `}
    >
      {/* Main rating bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3">
        <span className="text-sm text-gray-700 font-medium mb-2 sm:mb-0 sm:pr-2">
          {label}
        </span>
        
        <div className="flex items-center justify-center gap-1 flex-shrink-0">
          {EMOJI_RATINGS.map((emoji) => (
            <button
              key={emoji.rating}
              onClick={() => handleEmojiClick(emoji.rating)}
              className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-sm sm:text-base transition-all duration-200 hover:scale-110 ${
                selectedRating === emoji.rating
                  ? `${emoji.color} bg-gray-100 scale-110 shadow-md`
                  : 'text-gray-400 hover:text-gray-600'
              }`}
              disabled={isSubmitted}
              title={emoji.label}
            >
              {emoji.emoji}
            </button>
          ))}
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && !isSubmitted && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 bg-gray-50">
          {selectedEmoji && (
            <div className="flex items-center gap-3 pt-3 pb-1">
              <span className="text-2xl">{selectedEmoji.emoji}</span>
              <div>
                <div className={`font-semibold text-sm ${selectedEmoji.color}`}>
                  {selectedEmoji.label}
                </div>
                <div className="text-xs text-gray-500">
                  {selectedRating}/5 estrelas
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Quer nos contar mais?
            </label>
            <textarea
              ref={textAreaRef}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Compartilhe sua experiência..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm resize-none bg-white"
            />
          </div>
          
          <div className="flex justify-between items-center pt-1">
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <MessageCircle size={12} />
              <span>Feedback confidencial</span>
            </div>
            
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader size={14} className="animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Success state */}
      {isSubmitted && (
        <div className="px-4 pb-4 pt-3 border-t border-gray-100 bg-green-50">
          <div className="flex flex-col items-center justify-center gap-2 text-green-600 py-2">
            <CheckCircle size={24} />
            <div className="text-center">
              <div className="font-semibold text-sm">Feedback recebido!</div>
              <div className="text-xs text-green-600">Obrigado por sua opinião</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};